import { useState } from "react";
import { Megaphone, Globe, Lock, Trash2, Send } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";

function timeAgo(iso) {
  const d = (Date.now() - new Date(iso).getTime()) / 1000;
  if (d < 60) return "just now";
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
}

// canPost: leader only. readOnly: public viewer (hide compose entirely).
export function AllianceAnnouncements({ a, reload, canPost = false }) {
  const [body, setBody] = useState("");
  const [visibility, setVisibility] = useState("alliance");
  const [busy, setBusy] = useState(false);
  const items = a.announcements || [];

  const post = async () => {
    if (body.trim().length < 2) return toast.error("Write an announcement first");
    setBusy(true);
    try {
      await api.post("/alliance/announcements", { body: body.trim(), visibility });
      setBody("");
      toast.success("Announcement posted");
      reload();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    try { await api.delete(`/alliance/announcements/${id}`); toast.success("Announcement removed"); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  if (!canPost && items.length === 0) return null;

  return (
    <section data-testid="alliance-announcements" className="border border-amber-900/30 bg-zinc-950 p-5">
      <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm mb-3 flex items-center gap-2">
        <Megaphone size={16} /> Announcements
      </h3>

      {canPost && (
        <div className="mb-4 border border-amber-900/30 bg-black/30 rounded-sm p-3">
          <textarea data-testid="announcement-input" value={body} onChange={(e) => setBody(e.target.value)}
            rows={3} maxLength={2000} placeholder="Post an announcement to your alliance…"
            className="w-full bg-zinc-900 border border-zinc-800 text-zinc-200 text-sm px-3 py-2 outline-none focus:border-amber-600 rounded-sm resize-none" />
          <div className="flex flex-wrap items-center gap-2 mt-2">
            <span className="text-[11px] uppercase tracking-widest text-zinc-500">Visibility</span>
            <button data-testid="announcement-vis-alliance" onClick={() => setVisibility("alliance")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors ${visibility === "alliance" ? "bg-amber-500 text-zinc-950 border-amber-500 font-semibold" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
              <Lock size={12} /> Alliance only
            </button>
            <button data-testid="announcement-vis-public" onClick={() => setVisibility("public")}
              className={`flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-sm border transition-colors ${visibility === "public" ? "bg-amber-500 text-zinc-950 border-amber-500 font-semibold" : "border-zinc-800 text-zinc-400 hover:border-amber-700/50"}`}>
              <Globe size={12} /> Public
            </button>
            <button data-testid="announcement-post-btn" onClick={post} disabled={busy}
              className="ml-auto inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-cinzel uppercase tracking-wide bg-amber-600 text-zinc-950 hover:bg-amber-500 rounded-sm transition-colors disabled:opacity-50">
              <Send size={14} /> Post
            </button>
          </div>
        </div>
      )}

      {items.length === 0 ? (
        <p className="text-sm text-zinc-600">No announcements yet.</p>
      ) : (
        <div className="space-y-3">
          {items.map((an) => (
            <div key={an.id} data-testid={`announcement-${an.id}`} className="border-l-2 border-amber-700/50 bg-black/20 pl-3 pr-2 py-2">
              <div className="flex items-center gap-2 text-[11px] text-zinc-500 mb-1">
                <span className="text-amber-400/90">{an.author_name}</span>
                <span>· {timeAgo(an.created_at)}</span>
                <span className={`inline-flex items-center gap-1 uppercase tracking-wider px-1.5 py-0.5 rounded-sm ${an.visibility === "public" ? "text-sky-300 bg-sky-950/40" : "text-zinc-400 bg-zinc-900"}`}>
                  {an.visibility === "public" ? <><Globe size={10} /> Public</> : <><Lock size={10} /> Alliance</>}
                </span>
                {canPost && (
                  <button data-testid={`announcement-delete-${an.id}`} onClick={() => remove(an.id)}
                    className="ml-auto text-zinc-600 hover:text-red-400"><Trash2 size={13} /></button>
                )}
              </div>
              <p className="text-sm text-zinc-200 whitespace-pre-line leading-relaxed">{an.body}</p>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}
